import configJSON from 'config.json';
import { CountryCodeList } from 'types/countries';
import { SiteLanguage } from 'types/locales';
import ExprealtyCaTheme from './theme-config';
import { createHomeConfig } from './home-config';
import { createJoinExpConfig } from './join-exp-config';
import {
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID_TYPE,
  ContentHosts,
  DEFAULT_GTM_CONTAINER_ID_KEY,
} from '@tenants/types';
import { createFooterConfig } from './footer-config';
import { createHeaderConfig } from './header-config';
import { createOrganizationConfig } from './organization-config';
import { Namespaces } from 'types/locales';
import { createLanguageConfig } from '@tenants/utils/configs';

import type {
  HomeConfig,
  JoinExpConfig,
  Tenant,
  TenantConfigHome,
  TenantConfigJoinExp,
  FooterConfig,
  TenantConfigFooter,
  TenantConfigFilters,
  HeaderConfig,
  TenantConfigHeader,
  OrganizationConfig,
  TenantConfigOrganization,
  TenantConfigSEO,
} from '@tenants/types';
import { canShowSchoolsCanadian } from '@tenants/definitions/shared/features';

//#region Public Constants
export const EXP_REALTY_CA_L18N_NAMESPACES = [
  Namespaces.AGENT_SEARCH,
  Namespaces.EXP_REALTY_CA,
  Namespaces.JOIN_EXP_CA,
  Namespaces.HOME_APPRAISAL,
  Namespaces.COMMON,
  Namespaces.SEARCH,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
  Namespaces.LISTING,
  Namespaces.CONNECTION_REQUEST,
  Namespaces.CONNECT_WITH_AGENT,
  Namespaces.INTERNAL_LINKS,
  Namespaces.HOME,
] as const;

// TODO: Improve the approach once we roll this out to production
// eslint-disable-next-line enforce-localization-testing/enforce-localization-testing
const allowGlobalLocaleSwitching = true;

export const JOIN_EXP_CA_GTM_CONTAINER_ID_KEY = 'JoinExpCa' as const;
//#endregion

//#region Public Types
export type ExpRealtyCaTenantConfig = TenantConfigHome & TenantConfigJoinExp & TenantConfigFooter & TenantConfigFilters & TenantConfigOrganization & TenantConfigHeader & TenantConfigSEO;
export type ExpRealtyCaTenant = Tenant<EXP_REALTY_CA_TENANT_ID_TYPE, typeof ExprealtyCaTheme, ExpRealtyCaTenantConfig, typeof JOIN_EXP_CA_GTM_CONTAINER_ID_KEY>;
//#endregion

//#region Config Factories
const header: HeaderConfig = createHeaderConfig();
const home: HomeConfig = createHomeConfig();
const joinExp: JoinExpConfig = createJoinExpConfig();
const footer: FooterConfig = createFooterConfig();
const organization: OrganizationConfig = createOrganizationConfig();
//#endregion

//#region Tenant Definition
export const expRealtyCATenant: ExpRealtyCaTenant = {
  id: EXP_REALTY_CA_TENANT_ID,
  hosts: configJSON.hosts[EXP_REALTY_CA_TENANT_ID],
  contentHosts: configJSON.contentPageApi[EXP_REALTY_CA_TENANT_ID] as ContentHosts,
  theme: ExprealtyCaTheme,
  get localization() {
    return {
      ...createLanguageConfig({
        globalLanguages: [SiteLanguage.EnglishCA, SiteLanguage.FrenchCA],
        standardLanguages: [SiteLanguage.English, SiteLanguage.French],
        globalDefaultLanguage: SiteLanguage.EnglishCA,
        standardDefaultLanguage: SiteLanguage.English,
        namespaces: EXP_REALTY_CA_L18N_NAMESPACES,
        allowGlobalLocaleSwitching,
      }),
      countries: [CountryCodeList.CANADA],
      defaultCountry: CountryCodeList.CANADA,
      switchingEnabled: true,
    };
  },
  config: {
    header,
    home,
    joinExp,
    footer,
    organization,
    filters: {
      countryCode: CountryCodeList.CANADA,
    },
    seo: {
      useLocaleAwareCanonicals: allowGlobalLocaleSwitching,
      useLocaleAwareHrefLang: allowGlobalLocaleSwitching,
    },
  },
  routes: {
    claimed: '*',
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${EXP_REALTY_CA_TENANT_ID}"`,
    forceTenantFeatureValue: EXP_REALTY_CA_TENANT_ID,
  },
  get features() {
    return {
      MyLinkMyLead: false,
      AgentSearch: true,
      AccessibilityWidget: true,
      TrustArc: {
        enabled: true,
        domain: 'exprealty.com',
      },
      AddressPage: {
        showSchools: canShowSchoolsCanadian,
      },
      ShowProfileLocaleSwitcher: allowGlobalLocaleSwitching,
      HasMultilingualContentPages: true,
      UseListingCardV2: allowGlobalLocaleSwitching,
      ShowQuebecPopup: !allowGlobalLocaleSwitching,
      ShowAgentCardsOnAddressPage: true,
    };
  },
  googleTagManager: {
    containerIds: {
      [DEFAULT_GTM_CONTAINER_ID_KEY]: configJSON.googleTagManager.containerIdExpCa,
      [JOIN_EXP_CA_GTM_CONTAINER_ID_KEY]: configJSON.googleTagManager.containerIdJoinExp,
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID_EXP || '',
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID_EXP || '',
  },
  sitemap: {
    urls: [
      'https://www.exprealty.ca/sitemaps/area-pages/sitemap.xml',
      'https://www.exprealty.ca/sitemaps/address-pages/sitemap.xml',
      'https://www.exprealty.ca/sitemaps/root-pages/sitemap-index.xml',
    ],
  },
  robots: {
    disallowDomains: [],
  },
  pvr: {
    phoneNumber: '1-844-248-2174',
  },
  mobileAppBanner: null,
};
//#endregion
